import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@/common/components/Button/Button';
import { ControlledCheckbox } from '@/common/components/Fields/ControlledCheckbox/ControlledCheckbox';
import { ControlledCountryField } from '@/common/components/Fields/ControlledCountryField/ControlledCountryField';
import { ControlledTextField } from '@/common/components/Fields/ControlledTextField/ControlledTextField';
import { Form } from '@/common/components/Form/Form';
import { Grid } from '@/common/components/Grid/Grid';
import { Modal } from '@/common/components/Modal/Modal';
import { Stack } from '@/common/components/Stack/Stack';
import { MARKET } from '@/common/constants/markets';
import { useEmployeeDetails } from '@/common/contexts/user/UserDetailsContext';
import { useFeatures } from '@/common/integrations/feature-flag/feature-flag.hooks';
import { useUpdateEmployeeDetails } from '@/common/integrations/menhir/employee/employee/employee.hooks';
import { IEmployee } from '@/common/integrations/menhir/employee/employee/employee.types';
import { useGetInsurancePolicies } from '@/common/integrations/menhir/employee/insurance/insurance.hooks';
import { notify } from '@/common/utils/toast/toast';
import { useCountries } from '@/web/hooks/useCountries';
import { useMarket } from '@/web/hooks/useMarket';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  TAddressFormValues,
  getAddressFormSchema,
} from './AddressFormModal.schema';

interface IAddressFormModalProps {
  employee: IEmployee;
  onClose: () => void;
}

export const AddressFormModal = ({
  employee,
  onClose,
}: IAddressFormModalProps) => {
  const { t } = useTranslation();
  const {
    features: { ff_share_details_with_insurer },
  } = useFeatures();
  const { company } = useEmployeeDetails();
  const { mutateAsync: updateEmployeeDetails } = useUpdateEmployeeDetails();
  const { getCountryByCode } = useCountries();
  const initialShareWithInsurer = employee.address.shareWithInsurer ?? false;
  const { policies } = useGetInsurancePolicies();
  const healthPolicy = policies?.find(
    (policy) => policy.product?.plan?.type === 'health'
  );
  const { slug } = useMarket();

  const methods = useForm<TAddressFormValues>({
    mode: 'onTouched',
    defaultValues: {
      address: employee.address.addressLine1,
      zipCode: employee.address.zipCode,
      city: employee.address.city,
      country: getCountryByCode(employee.address.country),
      shareWithInsurer: initialShareWithInsurer,
      province: employee.address.district,
    },
    resolver: zodResolver(getAddressFormSchema(company.market.slug)),
  });

  const onSubmit = (close: () => void) => async (form: TAddressFormValues) => {
    const {
      address,
      zipCode,
      city,
      country: formCountry,
      shareWithInsurer,
      province,
    } = form;
    try {
      await updateEmployeeDetails({
        employeeId: employee.id,
        payload: {
          address: {
            address_line_1: address,
            zipcode: zipCode,
            city: city,
            country: formCountry?.iso2 ?? '',
            share_with_insurer: shareWithInsurer,
            district: province,
          },
        },
      });
      notify({
        message:
          shareWithInsurer !== initialShareWithInsurer
            ? t(
                'personal_details.address.form.share_with_insurer.update_message'
              )
            : t('general.alert.success.changes'),
        type: 'success',
      });
      close();
    } catch {
      notify({
        message: t('errors.api.generic'),
        type: 'error',
      });
    }
  };

  const canUpdateAddressDetails = () => {
    if (!ff_share_details_with_insurer || !healthPolicy) {
      return false;
    }
    return healthPolicy.persons.some(
      (person) =>
        person.type === 'employee' &&
        ['active', 'pending'].includes(person.status)
    );
  };

  return (
    <Modal
      title={t('dashboard.personal_info.sections.address.title')}
      onClose={() => onClose()}
      footer={({ close }) => (
        <Stack horizontal gap="md">
          <Button
            variant="secondary"
            onPress={close}
            disabled={methods.formState.isSubmitting}
          >
            {t('dashboard.personal_info.cancel_button')}
          </Button>
          <Button
            type="submit"
            className="flex-1"
            loading={methods.formState.isSubmitting}
            onPress={methods.handleSubmit(onSubmit(close))}
          >
            {t('dashboard.personal_info.save_button')}
          </Button>
        </Stack>
      )}
    >
      {({ close }) => (
        <FormProvider {...methods}>
          <Form onSubmit={methods.handleSubmit(onSubmit(close))}>
            <Stack gap="md">
              <ControlledTextField
                name="address"
                type="text"
                label={t(
                  'dashboard.personal_info.sections.address.labels.address'
                )}
              />
              <Grid gap="md" columnBasis="basis-[16rem]">
                <ControlledTextField
                  name="zipCode"
                  type="text"
                  label={t(
                    'dashboard.personal_info.sections.address.labels.zipcode'
                  )}
                />
                <ControlledTextField
                  name="city"
                  type="text"
                  label={t(
                    'dashboard.personal_info.sections.address.labels.city'
                  )}
                />
                {slug === MARKET.ES ? (
                  <ControlledTextField
                    name="province"
                    label={t('personal_details.address.form.province.label')}
                  />
                ) : null}
                <ControlledCountryField
                  name="country"
                  label={t('personal_details.address.form.country.label')}
                />
              </Grid>
              {canUpdateAddressDetails() && (
                <ControlledCheckbox<TAddressFormValues>
                  name="shareWithInsurer"
                  label={t(
                    'personal_details.address.form.share_with_insurer.label'
                  )}
                />
              )}
            </Stack>
          </Form>
        </FormProvider>
      )}
    </Modal>
  );
};
